import { isString } from 'mathjs';
import Action from '../../classes/Action';
import AssetUtility from '../../classes/AssetUtility';
import MaterialAsset from '../../classes/MaterialAsset';
import ARTracker from '../../classes/ARTracker';
import store, { StoreActions } from '../../state/store';
import { AssetTypes } from '../../globals';
import { traverseGrapesProject } from '../../components/visageUI/GrapesJSUtilities';
import { JsonCopy } from '../../classes/utilities/Misc';
import { GjsAttributeNames } from '../../components/visageUI/gjsplugin/Enums';
import { CssProperties, embeddedImagesBase64, IGrapesJSComponent, IGrapesJSProjectData } from '../../interfaces/VisageUI';
import AssetsManager from '../../classes/AssetsManager';

export default class ReplaceImageAsset extends Action
{
	constructor(oldAssetId: string, newAssetId: string)
	{
		const oldAssetName = AssetUtility.getAssetName(oldAssetId);
		const newAssetName = AssetUtility.getAssetName(newAssetId);
		const actionName = "Replaced " + oldAssetName + " References With " + newAssetName;
		super([oldAssetId, newAssetId], [newAssetId, oldAssetId], actionName, [oldAssetId, newAssetId]);
	}

	do(oldAssetId: string, newAssetId: string) : void
	{
		this.replaceImagesInMaterials(oldAssetId, newAssetId);
		this.replaceImagesInTrackers(oldAssetId, newAssetId);
		this.replaceImagesInOldUI(oldAssetId, newAssetId);
		this.replaceImagesInVisageUI(oldAssetId, newAssetId);
	}

	verify(oldAssetId: string, newAssetId: string): boolean
	{
		return 	oldAssetId !== newAssetId &&
				isString(oldAssetId) && isString(newAssetId) && 
				AssetsManager.getAssetInfo(oldAssetId)?.type === AssetTypes.Image && 
				AssetsManager.getAssetInfo(newAssetId)?.type === AssetTypes.Image;
	}

	replaceImagesInMaterials(oldAssetId: string, newAssetId: string)
	{
		for (const materialAsset of MaterialAsset.materialAssetBank.values())
		{
			for (const key of Object.keys(materialAsset))
			{
				if (!key.includes("initial_") && materialAsset[key] && materialAsset[key].isTexture && materialAsset[key].userData.assetId === oldAssetId)
				{
					materialAsset.handleChange(key, newAssetId, false);

					// update redux manually
					store.dispatch(StoreActions.material.setMaterialProperty({
						id: materialAsset.guid,
						value: newAssetId,
						property: key,
						shouldSubmit: false
					}));
				}
			}
		}
	}

	replaceImagesInTrackers(oldAssetId: string, newAssetId: string)
	{
		const trackers = ARTracker.getTrackerList() as ARTracker[];
		for (const tracker of trackers)
		{
			if (tracker.getImage() === oldAssetId)
			{
				tracker.setImage(newAssetId);
			}
		}
	}

	replaceImagesInOldUI(oldAssetId: string, newAssetId: string)
	{
		store.dispatch(StoreActions.ui.replaceAsset({ oldId: oldAssetId, newId: newAssetId }));
	}

	replaceImagesInVisageUI(oldAssetId: string, newAssetId: string)
	{
		const replaceAssetIdInComponent = (component: IGrapesJSComponent, projectData: IGrapesJSProjectData) =>
		{
			const imageUrl = AssetUtility.getPreviewAssetURL(newAssetId) ?? embeddedImagesBase64.imagePlaceholder;

			if (component.attributes?.[GjsAttributeNames.AssetID] === oldAssetId)
			{
				component.attributes[GjsAttributeNames.AssetID] = newAssetId;
				component.attributes[GjsAttributeNames.ImageSource] = imageUrl;
			}

			if(component.attributes?.[GjsAttributeNames.CoverAssetID] === oldAssetId)
			{
				component.attributes[GjsAttributeNames.CoverAssetID] = newAssetId;

				for(const style of projectData.styles)
				{
					if(style.selectors[0] === `#${component.attributes.id}`)
					{
						style.style[CssProperties.Background] = `url('${imageUrl}')`;
					}
				}
			}
		}

		// step ui 
		for(const [stepId, uiData] of Object.entries(store.getState().visageModule.stepUIData))
		{
			const projectData = JsonCopy(uiData);
			traverseGrapesProject(projectData, ({ component, projectData }) => replaceAssetIdInComponent(component, projectData));
			store.dispatch(StoreActions.visage.setStepUIData({ stepId: stepId, data: projectData }));
		}

		// templates - need to replace both current and previous states
		for(const [templateId, templateData] of Object.entries(store.getState().visageModule.uiTemplateData))
		{
			const template = JsonCopy(templateData);
			traverseGrapesProject(template.content, ({ component, projectData }) => replaceAssetIdInComponent(component, projectData));

			if(template.previousState)
			{
				traverseGrapesProject(template.previousState, ({ component, projectData }) => replaceAssetIdInComponent(component, projectData));
			}

			store.dispatch(StoreActions.visage.setUITemplateData({ templateId: templateId, data: template }));
		}
	}
}
